import React, { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import ActivityFilters from "./ActivityFilters";
import ActivityList from "./ActivityList";
import PaginationControls from "./PaginationControls";
import ExportPDFButton from "./ExportPDFButton";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";
import EmptyState from "../common/EmptyState";
import useActivities from "../../../hooks/useActivities";
import useActivityFilters from "../../../hooks/useActivityFilters";
import Toast from "../common/Toast";

/**
 * Main activities section with filters, list, and pagination
 */
const ActivitiesSection = ({ userId, studentId, onEdit, onEditActivity, onNewActivity, userProfile }) => {
  const handleEditClick = onEdit || onEditActivity;
  const {
    activities,
    totalActivities,
    currentPage,
    totalPages,
    loading,
    error,
    loadActivities,
    loadAllForPDF,
    removeActivity,
  } = useActivities();

  const {
    selectedType,
    setSelectedType,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    statusFilter,
    setStatusFilter,
    attemptFilter,
    setAttemptFilter,
    currentFilters,
    hasActiveFilters,
    resetFilters,
  } = useActivityFilters();

  const [toast, setToast] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);

  // Load activities on mount and when filters/page changes
  useEffect(() => {
    loadActivities(currentPage, currentFilters);
  }, [currentPage, currentFilters, loadActivities]);

  // Extract unique activity types
  useEffect(() => {
    if (activities.length > 0) {
      const types = [...new Set(activities.map((a) => a.type).filter(Boolean))];
      setActivityTypes(types);
    }
  }, [activities]);

  const handlePageChange = (page) => {
    loadActivities(page, currentFilters);

    // Scroll to top of section
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = () => {
    // Reset to page 1 when filters change
    loadActivities(1, currentFilters);
  };

  const handleDelete = async (activityId) => {
    try {
      await removeActivity(activityId);
      setToast({ type: "success", message: "Activity deleted successfully" });

      // Reload current page
      loadActivities(currentPage, currentFilters);
    } catch (err) {
      setToast({ type: "error", message: "Failed to delete activity" });
    }
  };

  const handleResetFilters = () => {
    resetFilters();
    loadActivities(1, {});
  };

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Activities</h2>
          <p className="text-sm text-gray-600 mt-1">
            {totalActivities}{" "}
            {totalActivities === 1 ? "activity" : "activities"} total
          </p>
        </div>

        <ExportPDFButton
          currentFilters={currentFilters}
          hasActiveFilters={hasActiveFilters}
          onLoadAllActivities={loadAllForPDF}
          userProfile={userProfile}
        />
      </div>

      {/* Filters */}
      <ActivityFilters
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        attemptFilter={attemptFilter}
        setAttemptFilter={setAttemptFilter}
        activityTypes={activityTypes}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Error State */}
      {error && <ErrorMessage message={error} type="error" />}

      {/* Loading State */}
      {loading && <LoadingSpinner text="Loading activities..." />}

      {/* Empty State */}
      {!loading && !error && activities.length === 0 && (
        <EmptyState
          icon={FileText}
          title={
            hasActiveFilters
              ? "No activities match your filters"
              : "No activities yet"
          }
          description={
            hasActiveFilters
              ? "Try adjusting your filters or reset them to see all activities"
              : "Start by creating your first activity to track your progress"
          }
          actionLabel={hasActiveFilters ? null : "Create Activity"}
          onAction={hasActiveFilters ? null : onNewActivity}
        />
      )}

      {/* Activities List */}
      {!loading && !error && activities.length > 0 && (
        <>
          <ActivityList
            activities={activities}
            onEdit={handleEditClick}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalActivities}
              onPageChange={handlePageChange}
              loading={loading}
            />
          )}
        </>
      )}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ActivitiesSection;



